import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import Switch from "@mui/material/Switch";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import useAxios from "../useAxios";
import { useSelector } from "react-redux";
import ModernLoder, {
  CategoryDetailsLoader,
  RegistrationIdeasLoader,
  ProductDetailLoader,
  ProductCategoriesLoader,
  ProductDetailSkeleton,
  UsersListSkeleton
} from "./ModernLoder";

const UsersList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const istab = useMediaQuery(theme.breakpoints.down("md"));
  const isSmal = useMediaQuery(theme.breakpoints.down("xs"));
  const user_type = useSelector((state) => state.auth.user_type);
  const loggedInemail = useSelector((state) => state.auth.email);
  const token = useSelector((state) => state.auth.token);
  const axiosData = useAxios();
  const [loading, setLoading] = useState(false);
  const [userList, setUserList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);

  // Get User list from database
  useEffect(() => {
    setLoading(true);
    axiosData
      .get(`getAllUser`)
      .then((data) => {
        const getUser = data.data.user.filter(
          (user) => user?.email !== loggedInemail
        );
        setUserList(getUser);
        setFilteredUsers(getUser);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error", error);
        setLoading(false);
      });
  }, []);

  // Handler for search input change
  const handleSearch = () => {
    const searchValue = searchTerm.toLowerCase();
    const filtered = userList.filter(
      (user) =>
        user.name.toLowerCase().includes(searchValue) ||
        user.email.toLowerCase().includes(searchValue) ||
        user.city?.toLowerCase().includes(searchValue) ||
        user.mobile_number?.toString().includes(searchValue)
    );
    setFilteredUsers(filtered);
  };

  const handleStatusChange = async (userId, currentStatus) => {
    const newStatus = currentStatus;
    try {
      // API call to update status
      const response = await axiosData.put(`getUser`, {
        _id: userId,
        status: newStatus,
      });
      // Update local state
      setFilteredUsers((prevUsers) =>
        prevUsers.map((user) =>
          user._id === userId
            ? { ...user, status: response.data.user.status }
            : user
        )
      );
    } catch (error) {
      console.error("Error updating user status:", error);
      // Optionally show error to user
      alert("Failed to update user status");
    }
  };

  if (loading)
    return (
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        minHeight={200}
      >
        <UsersListSkeleton/>
      </Box>
    );

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "center" : "center"}
        mb={3}
        gap={isMobile ? 2 : 0}
      >
        <Typography
          variant={isMobile ? "h5" : "h3"}
          sx={{
            fontWeight: 600,
            color: "primary.main",
          }}
        >
          Users List
        </Typography>
        <Box
          display="flex"
          alignItems="center"
          textAlign="center"
          flexDirection={isMobile ? "column" : "row"}
          width={isMobile ? "100%" : "auto"}
        >
          <TextField
            size="small"
            fullWidth={isMobile}
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mr: 2 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            fullWidth={isMobile}
            sx={{
              mt: isMobile ? 2 : 0,
              alignItems: "center",
              width: isMobile ? "50px" : "auto",
            }}
          >
            Search
          </Button>
        </Box>
      </Box>

      {isMobile || istab || isSmal ? (
        // Mobile view - card list
        <Box>
          {filteredUsers?.length > 0 ? (
            filteredUsers?.map((user, index) => (
              <Paper
                key={user._id || index}
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                }}
              >
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar
                    src={user.avatar}
                    sx={{
                      width: 48,
                      height: 48,
                      mr: 2,
                      boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Box>
                    <Typography fontWeight={600}>{user.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Mobile:
                  </Typography>
                  <Typography>{user.mobile_number || "N/A"}</Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    City:
                  </Typography>
                  <Typography>{user.city || "N/A"}</Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Joined:
                  </Typography>
                  <Typography>
                    {new Date(user.createdAt).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </Typography>
                </Box>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={2}
                >
                  <Box display="flex" alignItems="center">
                    <Switch
                      checked={user.status === "Active"}
                      onChange={() =>
                        handleStatusChange(
                          user._id,
                          user.status === "Active" ? "Inactive" : "Active"
                        )
                      }
                      color="success"
                      size="small"
                    />
                    <Chip
                      label={user.status === "Active" ? "Active" : "Inactive"}
                      size="small"
                      sx={{
                        ml: 1,
                        backgroundColor:
                          user.status === "Active"
                            ? "rgba(46, 125, 50, 0.1)"
                            : "rgba(211, 47, 47, 0.1)",
                        color: user.status === "Active" ? "#2e7d32" : "#d32f2f",
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  {/* <Box>
                    <IconButton size="small">
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box> */}
                </Box>
              </Paper>
            ))
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "50vh",
                color: "text.disabled",
              }}
            >
              <PeopleOutlineIcon sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6">No users found</Typography>
              <Typography variant="body2" align="center">
                Try adjusting your search or filter
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        // Desktop view - table
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 3,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
            overflow: "hidden",
          }}
        >
          <Table sx={{ minWidth: 650 }} aria-label="users table">
            <TableHead
              sx={{
                bgcolor: "primary.main",
                background:
                  "linear-gradient(135deg, #ed4343ff 0%, #a10d0dff 100%)",
              }}
            >
              <TableRow>
                {[
                  "User Name",
                  "Mobile Number",
                  "City",
                  "Email",
                  "Register Date",
                  "Status",
                ].map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      color: "common.white",
                      fontWeight: 800,
                      fontSize: "0.875rem",
                      py: 2,
                      borderBottom: "none",
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers?.length > 0 ? (
                filteredUsers?.map((user, index) => (
                  <TableRow
                    key={user._id || index}
                    sx={{
                      "&:last-child td": { borderBottom: "none" },
                      transition: "background-color 0.2s",
                      "&:hover": { bgcolor: "action.selected" },
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Box display="flex" alignItems="center">
                        <Avatar
                          src={user.avatar}
                          sx={{
                            width: 36,
                            height: 36,
                            mr: 2,
                            boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Typography fontWeight={500}>{user.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={user.mobile_number}
                        color="default"
                        size="small"
                        sx={{
                          borderRadius: 1,
                          bgcolor: "background.paper",
                          boxShadow: "0px 1px 2px rgba(0,0,0,0.05)",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={user.city}
                        color="default"
                        size="small"
                        sx={{
                          borderRadius: 1,
                          bgcolor: "background.paper",
                          boxShadow: "0px 1px 2px rgba(0,0,0,0.05)",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Box display="flex" alignItems="center">
                        <CalendarTodayIcon
                          sx={{
                            color: "text.secondary",
                            fontSize: "1rem",
                            mr: 1,
                          }}
                        />
                        <Typography variant="body2">
                          {new Date(user.createdAt).toLocaleDateString(
                            undefined,
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            }
                          )}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Switch
                        checked={user.status === "Active"}
                        onChange={() =>
                          handleStatusChange(
                            user._id,
                            user.status === "Active" ? "Inactive" : "Active"
                          )
                        }
                        color="success"
                        inputProps={{ "aria-label": "user status" }}
                      />
                      <Chip
                        label={user.status === "Active" ? "Active" : "Inactive"}
                        size="small"
                        sx={{
                          ml: 1,
                          backgroundColor:
                            user.status === "Active"
                              ? "rgba(46, 125, 50, 0.1)"
                              : "rgba(211, 47, 47, 0.1)",
                          color:
                            user.status === "Active" ? "#2e7d32" : "#d32f2f",
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        color: "text.disabled",
                      }}
                    >
                      <PeopleOutlineIcon sx={{ fontSize: 48, mb: 1 }} />
                      <Typography variant="h6">No users found</Typography>
                      <Typography variant="body2">
                        Try adjusting your search or filter
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default UsersList;
